/**
 * The chosen plan's action line — the ONE "Get the advice document" on a step-3 screen, in every
 * module (owner, 26 Oct 2026: "so long as it's consistent across the modules").
 *
 * It sits on the chosen card, where the plan was just chosen and where the reader is looking; the
 * "You've chosen …" banner above keeps its sentence but no button, so the screen has one door. When
 * the chosen plan is not among the cards shown for the goal, the plans view puts this line above the
 * cards instead, so the door is never lost.
 */
import React from 'react';

export interface ChosenAdviceLineProps {
    /** "Marco and Juliana's" / "your" — the file the plan is saved with. */
    possessiveFile: string;
    onAdvice: () => void;
    /** "Customise this plan" — omitted where the screen has no hand-built door. */
    onCustomise?: (() => void) | null;
    /** Further links that belong beside it (the granny flat's family guide). */
    extra?: React.ReactNode;
    busy?: boolean;
    /** Above the cards, when the chosen plan is not one of them. */
    offCard?: boolean;
    testId?: string;
}

const link = 'font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900';

export function ChosenAdviceLine({ possessiveFile, onAdvice, onCustomise, extra, busy = false, offCard = false, testId = 'chosen-plan-strip' }: ChosenAdviceLineProps) {
    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200/70 border-l-4 border-l-emerald-500 bg-white px-3 py-2.5 text-[13px] text-slate-700 shadow-sm" data-testid={testId}>
            <span className="min-w-[160px] flex-1">
                {offCard ? <>The chosen plan is saved with {possessiveFile} file. It isn&apos;t one of the plans shown for this goal.</> : <>Saved with {possessiveFile} file.</>}
            </span>
            <button
                type="button"
                onClick={onAdvice}
                disabled={busy}
                aria-busy={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                data-testid="chosen-plan-advice"
            >
                Get the advice document →
            </button>
            {extra}
            {onCustomise && <button type="button" onClick={onCustomise} className={link}>Customise this plan</button>}
        </div>
    );
}

export default ChosenAdviceLine;
